/**
 * CUR-424 / CUR-609 — structure utilities for compliance-audit-template
 * `appliesWhen` conditions, shared by the client rule builder, the server's
 * routing-value integrity guards, and the template-run engine (one contract,
 * three consumers).
 *
 * A condition is the typed JSON object `ComplianceAuditAppliesWhen` (see
 * `@shared/types/compliance/compliance-audit-template`): exactly one clause,
 * discriminated by `operator` — `alwaysApply`, `==`/`!=` over a single value,
 * or `in`/`notIn` over a value list. `field` references a CLASSIFICATION
 * sub-step's `produces.name` verbatim (no technical slug exists, so field
 * names may contain spaces and any characters — there is no string grammar).
 *
 * The engine matches names and values loosely (AUD-ROUTE-03): case-insensitive
 * with whitespace/underscore normalisation, and every comparison here matches
 * the same way. Negated operators evaluate TRUE when the field never landed
 * (permissive negation, design D3) — see `evaluateAppliesWhen`.
 */

import type { ComplianceAuditAppliesWhen } from '../../types/compliance/compliance-audit-template';
import { parseComplianceAuditTemplateSubStepProduces } from '../../types/compliance/compliance-audit-template';

// A routing value some Classification sub-step produces — the vocabulary the
// applies-when builder offers: pick a field, pick one of its values.
export interface ComplianceAuditRoutingField {
    name: string;
    options: string[];
}

interface RoutingFieldSubStepSource {
    type: string;
    produces?: unknown;
}

interface RoutingFieldTreeSource {
    Steps: ReadonlyArray<{ SubSteps: ReadonlyArray<RoutingFieldSubStepSource> }>;
}

/**
 * All routing values the working version's CLASSIFICATION sub-steps produce,
 * in document order. Accepts the structure tree as served by
 * `getComplianceAuditTemplateVersion` (or the equivalent Prisma rows).
 */
export function routingFields(sections: ReadonlyArray<RoutingFieldTreeSource>): ComplianceAuditRoutingField[] {
    const fields: ComplianceAuditRoutingField[] = [];
    for (const section of sections) {
        for (const step of section.Steps) {
            for (const subStep of step.SubSteps) {
                if (subStep.type !== 'CLASSIFICATION') continue;
                const produces = parseComplianceAuditTemplateSubStepProduces(subStep.produces);
                if (!produces) continue;
                const name = produces.name.trim();
                if (name) {
                    fields.push({ name, options: produces.options });
                }
            }
        }
    }
    return fields;
}

function normalise(value: string): string {
    return value
        .toLowerCase()
        .replace(/[_\s]+/g, ' ')
        .trim();
}

/** Loose value equality (AUD-ROUTE-03): case/whitespace/underscore-insensitive. */
export function sameValue(a: string, b: string): boolean {
    return normalise(a) === normalise(b);
}

/** Field names match with the same loose normalisation as values. */
export function sameField(a: string, b: string): boolean {
    return normalise(a) === normalise(b);
}

/** A condition that actually constrains — every operator except `alwaysApply`. */
export type ComplianceAuditConditionClause = Exclude<ComplianceAuditAppliesWhen, { operator: 'alwaysApply' }>;

/**
 * True when the condition is unconditional. Absent (`null`/`undefined`) counts
 * as unconditional so read paths degrade safely; the column itself is required
 * and defaults to `{ operator: 'alwaysApply' }`.
 */
export function isAlwaysCondition(
    condition: ComplianceAuditAppliesWhen | null | undefined,
): condition is Extract<ComplianceAuditAppliesWhen, { operator: 'alwaysApply' }> | null | undefined {
    return condition == null || condition.operator === 'alwaysApply';
}

// Values may still carry technical underscores (new_business); show them as words.
function friendlyValue(value: string): string {
    return value.trim().replace(/_/g, ' ');
}

/**
 * Friendly sentence for a condition: "Always runs", "{field} is {value}",
 * "{field} is not {value}", "{field} is any of {a}, {b}",
 * "{field} is none of {a}, {b}". A malformed stored condition — a stored JSON
 * `null` or an unknown operator cast past the boundary — renders as
 * "Condition not readable" rather than a broken sentence or a false
 * "Always runs"; only an absent (`undefined`) condition is unconditional.
 */
export function friendlyCondition(condition: ComplianceAuditAppliesWhen | null | undefined): string {
    if (condition === null) return 'Condition not readable';
    if (isAlwaysCondition(condition)) return 'Always runs';
    switch (condition.operator) {
        case '==':
            return `${condition.field} is ${friendlyValue(condition.value)}`;
        case '!=':
            return `${condition.field} is not ${friendlyValue(condition.value)}`;
        case 'in':
            return `${condition.field} is any of ${condition.values.map(friendlyValue).join(', ')}`;
        case 'notIn':
            return `${condition.field} is none of ${condition.values.map(friendlyValue).join(', ')}`;
        default:
            return 'Condition not readable';
    }
}

/** Field names referenced by the condition — `[]` for `alwaysApply`, else one. */
export function referencedFields(condition: ComplianceAuditAppliesWhen | null | undefined): string[] {
    return isAlwaysCondition(condition) ? [] : [condition.field];
}

/** Does the condition reference the field (loose `sameField` matching)? */
export function conditionReferencesField(
    condition: ComplianceAuditAppliesWhen | null | undefined,
    fieldName: string,
): boolean {
    return !isAlwaysCondition(condition) && sameField(condition.field, fieldName);
}

/**
 * The routing values the condition references — the `value` of `==`/`!=` or
 * the `values` of `in`/`notIn` (for the options-removal guard). `[]` for
 * `alwaysApply`.
 */
export function referencedValues(condition: ComplianceAuditAppliesWhen | null | undefined): string[] {
    if (isAlwaysCondition(condition)) return [];
    switch (condition.operator) {
        case '==':
        case '!=':
            return [condition.value];
        case 'in':
        case 'notIn':
            return [...condition.values];
    }
}

export interface RewriteConditionResult {
    condition: ComplianceAuditAppliesWhen;
    changed: boolean;
}

/**
 * Rewrite the condition's `field` from `oldName` to `newName` (AUD-ROUTE-02
 * rename-refactor) when it loosely matches `oldName`. A trivial structural
 * replacement — no parsing, so it can never fail.
 */
export function rewriteConditionFieldName(
    condition: ComplianceAuditAppliesWhen,
    oldName: string,
    newName: string,
): RewriteConditionResult {
    if (isAlwaysCondition(condition) || !sameField(condition.field, oldName)) {
        return { condition, changed: false };
    }
    return { condition: { ...condition, field: newName.trim() }, changed: true };
}

// A semantic offence in an otherwise well-formed condition, for the mutation
// guards and the publish gate: the caller owns the step/sub-step context and
// composes the human-readable message from these parts.
export type AppliesWhenValidationIssue =
    | { kind: 'unknownField'; field: string }
    | { kind: 'unknownValue'; field: string; value: string; options: string[] };

/**
 * Semantic validation of a structurally-valid condition against the routing
 * values the version's CLASSIFICATION sub-steps produce: the `field` must be
 * produced by some Classification, and every referenced value must be loosely
 * within that field's options. Returns every offence (a published snapshot is
 * immutable, so the publish gate lists them all); `[]` means valid.
 */
export function validateAppliesWhen(
    condition: ComplianceAuditAppliesWhen | null | undefined,
    fields: ReadonlyArray<ComplianceAuditRoutingField>,
): AppliesWhenValidationIssue[] {
    if (isAlwaysCondition(condition)) return [];
    const field = fields.find((candidate) => sameField(candidate.name, condition.field));
    if (field === undefined) {
        return [{ kind: 'unknownField', field: condition.field }];
    }
    return referencedValues(condition)
        .filter((value) => !field.options.some((option) => sameValue(option, value)))
        .map((value) => ({ kind: 'unknownValue', field: condition.field, value, options: field.options }));
}

/** A value an earlier CLASSIFICATION sub-step landed for a routing field. */
export interface ComplianceAuditLandedValue {
    field: string;
    value: string;
}

/**
 * Deterministic condition evaluation against landed classification values —
 * the engine's `conditionMatches` (design D3). The first landed occurrence of
 * a field name wins; names and values match loosely. Permissive negation is
 * deliberate: a field with no landed value makes `==`/`in` FALSE but
 * `!=`/`notIn` TRUE — an unclassified field counts as "not that value".
 * A stored JSON `null` is malformed, not absent, and throws like an unknown
 * operator does; only `undefined` (no condition at all) evaluates TRUE.
 */
export function evaluateAppliesWhen(
    condition: ComplianceAuditAppliesWhen | null | undefined,
    landedValues: ReadonlyArray<ComplianceAuditLandedValue>,
): boolean {
    if (condition === null) {
        // A stored JSON null cast past the typed boundary — misrouting
        // silently as "always runs" is worse than failing the run.
        throw new Error('appliesWhen is a stored JSON null, not a structured condition');
    }
    if (isAlwaysCondition(condition)) return true;
    const landed = landedValues.find((entry) => sameField(entry.field, condition.field))?.value;
    switch (condition.operator) {
        case '==':
            return landed !== undefined && sameValue(landed, condition.value);
        case '!=':
            return landed === undefined || !sameValue(landed, condition.value);
        case 'in':
            return landed !== undefined && condition.values.some((value) => sameValue(landed, value));
        case 'notIn':
            return landed === undefined || !condition.values.some((value) => sameValue(landed, value));
        default:
            // A malformed stored condition cast past the typed boundary —
            // misrouting silently is worse than failing the run.
            throw new Error(`Unknown appliesWhen operator: ${String((condition as { operator?: unknown }).operator)}`);
    }
}
